// LivestreamSuperChatOverlay.jsx
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import UserAvatar from '../container/UserAvatar.js';

import { EMPTY_OBJECT } from '../resource/defaults.js';

import { color } from '../style/variables.js';

import DiamondIconSource from '../../img/ic-diamond-xs.svg';
import DropdownIconSource from '../../img/ic_dropdown_white.svg';

const LINE_HEIGHT = 24;

export const LivestreamSuperChatOverlay = ({
  userId = '',
  username = '',
  displayName = '',
  text = '',
  diamonds = 0,
  isDisplayNameEnabled = false,
  superChatOptionData = EMPTY_OBJECT,
}) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [isTextOverflow, setIsTextOverflow] = useState(false);
  const borderColor = superChatOptionData[diamonds]?.borderColor;
  const elementRef = useRef(null);
  const usingName =
    isDisplayNameEnabled && displayName ? displayName : `@${username}`;

  // Calculate text overflow after text or expanded state changed
  useEffect(() => {
    const timer = setTimeout(() => {
      if (elementRef.current) {
        setIsTextOverflow(
          elementRef.current.clientHeight < elementRef.current.scrollHeight
        );
      }
    });
    return () => {
      clearTimeout(timer);
    };
  }, [text, isTextExpanded, usingName]);

  return (
    <StyledLivestreamSuperChatMessage
      ref={elementRef}
      data-is_text_overflow={isTextOverflow}
      data-is_text_expanded={isTextExpanded}
      borderColor={borderColor ? `#${borderColor}` : 'transparent'}
      isTextExpanded={isTextExpanded}
      isTextOverflow={isTextOverflow}
      onClick={() => {
        if (isTextOverflow || isTextExpanded) {
          setIsTextExpanded(!isTextExpanded);
        }
      }}
    >
      <Avatar>
        <UserAvatar id={userId} size={LINE_HEIGHT} />
      </Avatar>
      <Name>{usingName}</Name>
      <SuperChatDiamonds>
        <img src={DiamondIconSource} alt="diamond" />
        {diamonds}
      </SuperChatDiamonds>
      <SuperChatText>{text}</SuperChatText>
      {isTextOverflow && !isTextExpanded && (
        <DropdownIcon src={DropdownIconSource} alt="dropdown" />
      )}
    </StyledLivestreamSuperChatMessage>
  );
};

LivestreamSuperChatOverlay.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  text: PropTypes.string,
  diamonds: PropTypes.number,
  isDisplayNameEnabled: PropTypes.bool,
  superChatOptionData: PropTypes.object,
};

const StyledLivestreamSuperChatMessage = styled.div.attrs(
  ({ borderColor, isTextExpanded, isTextOverflow }) => ({
    style: {
      borderColor,
      display: isTextExpanded ? 'block' : '-webkit-box',
      paddingRight: `${isTextOverflow && !isTextExpanded ? 26 : 8}px`,
    },
  })
)`
  position: relative;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  padding: 4px 8px;
  padding-right: 26px;

  cursor: pointer;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Avatar = styled.span`
  margin-right: 2px;
  width: ${LINE_HEIGHT}px;
  height: ${LINE_HEIGHT}px;
  display: inline-flex;
  align-items: center;
`;

const Name = styled.span`
  margin-right: 2px;
  line-height: ${LINE_HEIGHT}px;
  vertical-align: top;
  font-size: 12px;
  font-weight: 600;
  color: ${color.neutral[100]};
`;

const SuperChatDiamonds = styled.span`
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: ${LINE_HEIGHT}px;
  vertical-align: top;
  color: ${color.tealBlue};
  > img {
    margin-right: 2px;
    width: 14px;
    height: 14px;
    object-fit: contain;
  }
`;

const SuperChatText = styled.span`
  line-height: ${LINE_HEIGHT}px;
  vertical-align: top;
  font-size: 12px;
  font-weight: 400;
  color: ${color.neutral[100]};
  word-break: break-all;
`;

const DropdownIcon = styled.img`
  position: absolute;
  top: 50%;
  right: 8px;
  width: 16px;
  height: 16px;
  object-fit: contain;
  transform: translateY(-50%);
`;

export default LivestreamSuperChatOverlay;
